import { PickerItem, Picker } from "@react-spectrum/s2";
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Key } from 'react';
export interface OrgOption {
    key: string;
    label: string;
}
export interface BuildOrgItemsArgs {
    /** All org ids the user can switch between. */ userOrgIds?: string[];
    /** Map of org id → display name. */ orgNames?: Record<string, string>;
    /** The currently selected org id. */ selectedOrgId?: string | null;
    /** Display name of the currently selected org (falls back to the id). */ orgName?: string | null;
}
/**
 * Single source of truth for the organization option list. Shared by the
 * header {@link OrgSwitcher} picker and the mobile profile-menu org section so
 * the two placements never diverge. Guarantees the currently-selected org is
 * always present as an option, even in the brief window before `userOrgIds`
 * has populated.
 */ export function buildOrgItems({ userOrgIds = [], orgNames = {}, selectedOrgId, orgName }: BuildOrgItemsArgs): OrgOption[] {
    const items = userOrgIds.map((orgId)=>({
            key: orgId,
            label: orgNames[orgId] ?? orgId
        }));
    if (selectedOrgId && !items.some((item)=>item.key === selectedOrgId)) {
        items.unshift({
            key: selectedOrgId,
            label: orgName ?? orgNames[selectedOrgId] ?? selectedOrgId
        });
    }
    return items;
}
/** Accessible name shared by every org-switching control. */ export const ORG_SWITCH_LABEL = 'Switch organization';
export interface OrgSwitcherProps extends BuildOrgItemsArgs {
    /** Switch handler; omitted (or a single org) renders a disabled picker. */ onOrgSwitch?: (orgId: string) => void;
}
/**
 * The compact organization switcher shown in the top app bar. It is the single
 * source of truth for the header org picker; on mobile the bar hides it (see
 * `Navbar`'s `showOrgPicker`) and the same org list is surfaced inside the
 * profile menu instead, so switching behaviour never diverges between the two
 * placements.
 */ export const OrgSwitcher: React.FC<OrgSwitcherProps> = ({ orgName, userOrgIds = [], orgNames = {}, selectedOrgId, onOrgSwitch })=>{
    const { t } = useTranslation();
    const hasMultipleOrgs = userOrgIds.length > 1 && Boolean(onOrgSwitch);
    const orgItems = useMemo(()=>buildOrgItems({
            userOrgIds,
            orgNames,
            selectedOrgId,
            orgName
        }), [
        userOrgIds,
        orgNames,
        selectedOrgId,
        orgName
    ]);
    const handleOrgSelection = useCallback((key: Key | null)=>{
        if (key == null) return;
        const id = String(key);
        if (id !== selectedOrgId && onOrgSwitch) onOrgSwitch(id);
    }, [
        onOrgSwitch,
        selectedOrgId
    ]);
    if (!orgName && !selectedOrgId) return null;
    return (<Picker aria-label={t('components.orgSwitcher.switchLabel')} items={orgItems} value={selectedOrgId ?? null} onChange={handleOrgSelection} isDisabled={!hasMultipleOrgs} UNSAFE_className="navbar-org-picker" styles={" Zd171"}>
      {(item)=><PickerItem id={item.key}>{item.label}</PickerItem>}
    </Picker>);
};
export default OrgSwitcher;

import "macro-57fa0eae0cb9a4bf3445753af6ed7a05233133e7f22aff58f2d83edb10cc47dc.css";